import './style.scss'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    FiChevronDown,
    FiChevronsLeft,
    FiLogOut,
    FiSearch,
    FiUser,
} from 'react-icons/fi'
import { Flex, Text, tokens, UIProps } from 'src/componentv2/Base/ui'
import { Avatar } from 'src/componentv2/Base/Avatar'
import qoreIconDefault from 'src/assets/v2/img/qore_iconv2.svg'
import qoreIconCollapsed from 'src/assets/v2/img/qore_iconv3.svg'
import helpResourceIcon from 'src/assets/v2/img/message-question-square_icon.svg'

export interface SidebarSubItem {
    key: string
    label: React.ReactNode
    href?: string
    onClick?: () => void
}

export interface SidebarNavItem {
    key: string
    label: React.ReactNode
    icon?: React.ReactNode
    href?: string
    onClick?: () => void
    children?: SidebarSubItem[]
}

export interface SidebarUser {
    name: React.ReactNode
    role?: React.ReactNode
    avatarSrc?: string
    initials?: string
}

export interface SidebarProps extends UIProps {
    logo?: React.ReactNode
    navItems: SidebarNavItem[]
    activeKey?: string
    defaultActiveKey?: string
    onItemSelect?: (key: string) => void

    searchable?: boolean
    searchPlaceholder?: string
    searchValue?: string
    onSearchChange?: (value: string) => void

    collapsible?: boolean
    collapsed?: boolean
    defaultCollapsed?: boolean
    onCollapseChange?: (collapsed: boolean) => void

    showHelp?: boolean
    helpLabel?: React.ReactNode
    onHelpClick?: () => void

    user?: SidebarUser
    onViewProfile?: () => void
    onLogout?: () => void

    width?: string
    collapsedWidth?: string

    /** Below the tablet breakpoint the sidebar renders as an off-canvas drawer. */
    mobileOpen?: boolean
    onMobileClose?: () => void
}

const SIDEBAR_BG = '#101828'
const SIDEBAR_TEXT_MUTED = '#9CA3AF'

// Only plain-string labels can be text-matched; anything else (custom
// ReactNode labels) is treated as non-matching rather than thrown away.
function labelText(label: React.ReactNode): string {
    return typeof label === 'string' ? label : ''
}

export const Sidebar: React.FC<SidebarProps> = ({
                                                    logo,
                                                    navItems,
                                                    activeKey,
                                                    defaultActiveKey,
                                                    onItemSelect,
                                                    searchable = true,
                                                    searchPlaceholder = 'Search',
                                                    searchValue,
                                                    onSearchChange,
                                                    collapsible = true,
                                                    collapsed,
                                                    defaultCollapsed = false,
                                                    onCollapseChange,
                                                    showHelp = true,
                                                    helpLabel = 'Help & Resources',
                                                    onHelpClick,
                                                    user,
                                                    onViewProfile,
                                                    onLogout,
                                                    width = '28rem',
                                                    collapsedWidth = '8rem',
                                                    bgColor = SIDEBAR_BG,
                                                    className = '',
                                                    mobileOpen = false,
                                                    onMobileClose,
                                                    ...rest
                                                }) => {
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed)
    const isCollapsed = collapsed ?? internalCollapsed

    const [internalActive, setInternalActive] = useState(defaultActiveKey)
    const isActive = activeKey ?? internalActive

    // Falls back to internal state when the parent doesn't control search.
    const [internalSearch, setInternalSearch] = useState('')
    const query = (searchValue ?? internalSearch).trim().toLowerCase()

    const handleSearchChange = (value: string) => {
        setInternalSearch(value)
        onSearchChange?.(value)
    }

    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())

    // --- Profile dropdown -----------------------------------------------
    // Rendered via a portal to document.body and positioned with
    // getBoundingClientRect() so it can never be clipped by an ancestor's
    // `overflow: hidden/auto`, and never loses a stacking-context fight with
    // sibling page content (the sidebar is `position: sticky`, which creates
    // its own stacking context that a normal absolutely-positioned child
    // can't escape).
    const [profileOpen, setProfileOpen] = useState(false)
    const profileBtnRef = useRef<HTMLButtonElement>(null)
    const profileMenuRef = useRef<HTMLDivElement>(null)
    const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null)

    useEffect(() => {
        if (!profileOpen) return

        const updatePosition = () => {
            const btn = profileBtnRef.current
            if (!btn) return
            const rect = btn.getBoundingClientRect()
            setMenuPos({
                top: rect.bottom,
                left: rect.right + 12, // 1.2rem gap, mirrors old `margin-left: -1.2rem` + `left: 100%`
            })
        }

        updatePosition()
        window.addEventListener('scroll', updatePosition, true)
        window.addEventListener('resize', updatePosition)
        return () => {
            window.removeEventListener('scroll', updatePosition, true)
            window.removeEventListener('resize', updatePosition)
        }
    }, [profileOpen])

    // Close on outside click / Escape — the menu is no longer scoped by DOM
    // nesting once it's portaled, so this has to be handled explicitly.
    useEffect(() => {
        if (!profileOpen) return

        const handlePointerDown = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                profileMenuRef.current?.contains(target) ||
                profileBtnRef.current?.contains(target)
            ) {
                return
            }
            setProfileOpen(false)
        }

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setProfileOpen(false)
        }

        document.addEventListener('mousedown', handlePointerDown)
        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('mousedown', handlePointerDown)
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [profileOpen])
    // ----------------------------------------------------------------------

    const toggleCollapsed = () => {
        const next = !isCollapsed
        setInternalCollapsed(next)
        onCollapseChange?.(next)
    }

    const toggleExpanded = (key: string) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev)
            next.has(key) ? next.delete(key) : next.add(key)
            return next
        })
    }

    const handleSelect = (key: string, item: SidebarNavItem | SidebarSubItem) => {
        setInternalActive(key)
        onItemSelect?.(key)
        item.onClick?.()
        onMobileClose?.()
    }

    const activeParentKey = useMemo(() => {
        return navItems.find((item) => item.children?.some((c) => c.key === isActive))?.key
    }, [navItems, isActive])

    // Prefix-matches top-level items by label; if only children match, keeps
    // the parent visible but narrows it down to just the matching children.
    const filteredNavItems = useMemo(() => {
        if (!query) return navItems

        return navItems.reduce<SidebarNavItem[]>((acc, item) => {
            const parentMatches = labelText(item.label).toLowerCase().startsWith(query)
            const matchingChildren = item.children?.filter((c) =>
                labelText(c.label).toLowerCase().startsWith(query)
            )

            if (parentMatches) {
                acc.push(item)
            } else if (matchingChildren?.length) {
                acc.push({ ...item, children: matchingChildren })
            }
            return acc
        }, [])
    }, [navItems, query])

    return (
        <>
            {mobileOpen && <div className="sidebar-backdrop" onClick={onMobileClose} />}
            <Flex
                className={`sidebar ${isCollapsed ? 'sidebar--collapsed' : ''} ${mobileOpen ? 'sidebar--mobile-open' : ''} ${className}`}
                bgColor={bgColor}
                style={{
                    width: isCollapsed ? collapsedWidth : width,
                    boxSizing: 'border-box',
                    flexDirection: 'column',
                    height: '100vh',
                    position: 'sticky',
                    top: 0,
                }}
                {...rest}
            >
                <div className="sidebar__top">
                    <Flex className="sidebar__brand" items="center" gap="0.8">
                        {logo ?? (
                            <img
                                src={isCollapsed ? qoreIconCollapsed : qoreIconDefault}
                                alt="Qore"
                            />
                        )}
                    </Flex>
                </div>

                {collapsible && (
                    <button
                        type="button"
                        className="sidebar__collapse-btn"
                        onClick={toggleCollapsed}
                        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <FiChevronsLeft className={isCollapsed ? 'is-flipped' : ''} />
                    </button>
                )}

                {searchable && !isCollapsed && (
                    <label className="sidebar__search">
                        <FiSearch />
                        <input
                            type="text"
                            placeholder={searchPlaceholder}
                            value={searchValue ?? internalSearch}
                            onChange={(e) => handleSearchChange(e.target.value)}
                        />
                    </label>
                )}

                <nav className="sidebar__nav">
                    {filteredNavItems.map((item) => {
                        const hasChildren = !!item.children?.length
                        const expanded = query
                            ? true
                            : expandedKeys.has(item.key) || activeParentKey === item.key
                        const active = isActive === item.key || activeParentKey === item.key

                        return (
                            <div key={item.key} className="sidebar__nav-group">
                                <a
                                    href={item.href ?? '#'}
                                    className={`sidebar__nav-item ${active ? 'is-active' : ''}`}
                                    onClick={(e) => {
                                        if (!item.href) e.preventDefault()
                                        if (hasChildren) {
                                            toggleExpanded(item.key)
                                        } else {
                                            handleSelect(item.key, item)
                                        }
                                    }}
                                >
                                    {item.icon && <span className="sidebar__nav-icon">{item.icon}</span>}
                                    {!isCollapsed && (
                                        <>
                                            <span className="sidebar__nav-label">{item.label}</span>
                                            {hasChildren && (
                                                <FiChevronDown
                                                    className={`sidebar__nav-chevron ${expanded ? 'is-open' : ''}`}
                                                />
                                            )}
                                        </>
                                    )}
                                </a>

                                {hasChildren && expanded && !isCollapsed && (
                                    <div className="sidebar__submenu">
                                        {item.children!.map((sub) => (
                                            <a
                                                key={sub.key}
                                                href={sub.href ?? '#'}
                                                className={`sidebar__submenu-item ${
                                                    isActive === sub.key ? 'is-active' : ''
                                                }`}
                                                onClick={(e) => {
                                                    if (!sub.href) e.preventDefault()
                                                    handleSelect(sub.key, sub)
                                                }}
                                            >
                                                {sub.label}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </nav>

                <div className="sidebar__spacer" />

                {showHelp && (
                    <a
                        href="#"
                        className="sidebar__help"
                        onClick={(e) => {
                            e.preventDefault()
                            onHelpClick?.()
                        }}
                    >
                        <img src={helpResourceIcon} alt="" width={18} height={18} />
                        {!isCollapsed && <span>{helpLabel}</span>}
                    </a>
                )}

                {user && (
                    <div className="sidebar__profile-wrap">
                        <button
                            ref={profileBtnRef}
                            type="button"
                            className="sidebar__profile"
                            onClick={() => setProfileOpen((v) => !v)}
                        >
                            <Avatar src={user.avatarSrc} initials={user.initials} size="4" />
                            {!isCollapsed && (
                                <Flex style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Text
                                        fontSize="1.4"
                                        fontWeight="200"
                                        color={tokens.white}
                                        style={{ fontFamily: "'Gilmer Regular', sans-serif" }}
                                    >
                                        {user.name}
                                    </Text>
                                    {user.role && (
                                        <Text fontSize="1.2" color={tokens.white}>
                                            {user.role}
                                        </Text>
                                    )}
                                </Flex>
                            )}
                        </button>
                    </div>
                )}
            </Flex>

            {user &&
                profileOpen &&
                menuPos &&
                createPortal(
                    <div
                        ref={profileMenuRef}
                        className="sidebar__profile-menu sidebar__profile-menu--portaled"
                        style={{
                            position: 'fixed',
                            top: menuPos.top,
                            left: menuPos.left,
                            transform: 'translateY(-100%)', // bottom-align to trigger, matching old `bottom: 0`
                        }}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                setProfileOpen(false)
                                onViewProfile?.()
                            }}
                        >
                            <FiUser />
                            View profile
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setProfileOpen(false)
                                onLogout?.()
                            }}
                        >
                            <FiLogOut />
                            Logout
                        </button>
                    </div>,
                    document.body
                )}
        </>
    )
}

export default Sidebar